import type { Collaborator } from "@nlc/shared-types";
import type { Key } from "react-stately";

import { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useListData } from "react-stately";

import { Avatar } from "@/components/avatar";
import { Description, Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Tag } from "@/components/tag-group";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useCollaboratorPermissions } from "@/features/request/shared/useCollaboratorPermissions";
import { useGetProjectCollaborators } from "@/features/request/shared/UseGetProjectCollaborators.tsx";

import { CollaboratorsSkeleton } from "./CollaboratorsSkeleton";

export function CollaboratorsSelect({ className }: { className?: string }) {
  const methods = useEditRequestFormContext();
  const { data, isFetching } = useGetProjectCollaborators();
  const brandId = useWatch({ control: methods.control, name: "brandId" });
  const { canEdit, canManage } = useCollaboratorPermissions(brandId);
  const { updateField } = useFieldUpdate();
  const defaultItems = methods.getValues("collaboratorIds");

  const selectedList = useListData<Collaborator>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    if (data && defaultItems) {
      selectedList.remove(...selectedList.items.map((item) => item.id));
      selectedList.append(
        ...data.filter((item) => defaultItems.includes(Number(item.id))),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultItems]);

  if (!data) {
    return <CollaboratorsSkeleton />;
  }

  return (
    <Controller
      control={methods.control}
      name="collaboratorIds"
      render={({ field: { name, onBlur, onChange, value } }) => (
        <MultiSelectField className={className} isInvalid={isFetching}>
          <Label>Add collaborators</Label>
          <MultiSelect
            isDisabled={!canEdit}
            isItemRemovable={canManage}
            items={data.filter((item) => canManage(item))}
            key={brandId ?? "no-brand"}
            name={name}
            onBlur={onBlur}
            onItemAdd={(key) => {
              const newValue = [...(value || []), key];

              onChange(newValue);
              void updateField(name, newValue);
            }}
            onItemRemove={(key) => {
              const newValue = (value || []).filter((id: Key) => id !== key);

              onChange(newValue);
              void updateField(name, newValue);
            }}
            placeholder="Select"
            renderEmptyState={(inputValue) => (
              <span className="p-2 text-text-primary">
                {inputValue ? (
                  <>No results found for: {inputValue}</>
                ) : (
                  `No collaborators available`
                )}
              </span>
            )}
            selectedList={selectedList}
            tag={(item) => (
              <Tag isDisabled={!canManage(item)}>
                <Avatar
                  alt={item.textValue}
                  className="rounded-full size-3"
                  src={item.profilePicture}
                />
                {item.textValue}
              </Tag>
            )}
          >
            {(item) => (
              <MultiSelectItem textValue={String(item.id)}>
                {item.textValue}
              </MultiSelectItem>
            )}
          </MultiSelect>
          <Description>
            Adding a collaborator will give them access to view project details,
            uploaded project files and comments between our team and yours.
            Please make sure you are only adding collaborators who you want to
            access this project fully.
          </Description>
        </MultiSelectField>
      )}
    />
  );
}
